import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Sun, Moon } from 'lucide-react';
import axios from 'axios';
import { parseApiError } from '../lib/parseApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [impostazioni, setImpostazioni] = useState(null);

  useEffect(() => {
    const fetchImpostazioni = async () => {
      try {
        const response = await axios.get(`${API}/impostazioni/branding`);
        setImpostazioni(response.data);
      } catch (error) {
        // Endpoint pubblico best-effort: si usa il branding di default
        console.error('Branding non caricato:', error);
      }
    };
    fetchImpostazioni();
    
    // Seed admin on first load
    const seedAdmin = async () => {
      try {
        await axios.post(`${API}/seed-admin`);
      } catch (error) {
        // Best-effort: l'admin esiste già nella quasi totalità dei casi
        console.error('Seed admin non eseguito:', error);
      }
    };
    seedAdmin();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Inserisci email e password');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Accesso effettuato');
    } catch (error) {
      const message = parseApiError(error, 'Errore di accesso');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      {/* Image Side */}
      <div className="hidden md:block bg-muted relative overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80"
          alt="Technical equipment"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <h2 className="text-5xl font-black tracking-tight mb-4 text-cyan-400">
            GESTIONE & ASSISTENZA
          </h2>
          <p className="text-2xl font-bold text-cyan-400 tracking-wide">
            Sistema All-in-One per Professionisti
          </p>
        </div>
      </div>

      {/* Form Side with Vibrant Gradient Background */}
      <div className="flex items-center justify-center p-8 relative overflow-hidden">
        {/* Vibrant gradient background - teal/blue energy colors */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-cyan-700 to-blue-800 z-0"></div>
        
        {/* Animated glow effects for energy feel */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-emerald-400/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
        
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={toggleTheme}
          className="absolute top-4 right-4 rounded-none z-10 text-white hover:bg-white/10"
          data-testid="login-theme-toggle"
        >
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>

        <div className="w-full max-w-md space-y-8 z-10">
          <div className="text-center">
            {/* Logo Grande */}
            {impostazioni?.logo_base64 ? (
              <div className="mb-6 flex flex-col items-center gap-3">
                <div className="bg-white/95 rounded-2xl p-4 shadow-2xl">
                  <img 
                    src={`data:image/png;base64,${impostazioni.logo_base64}`} 
                    alt="Logo" 
                    className="h-24 w-auto mx-auto object-contain"
                    data-testid="login-logo"
                  />
                </div>
              </div>
            ) : (
              <h1 className="text-5xl font-black tracking-tight mb-6 text-white drop-shadow-lg">
                {impostazioni?.nome_azienda || 'VELTA SISTEMI'}
              </h1>
            )}
            <p className="text-xs font-mono uppercase tracking-wider text-cyan-200">
              Accedi al sistema CRM
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-white/80">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                name="login-email"
                autoComplete="new-password"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="rounded-none h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-cyan-300"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-white/80">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="login-password"
                  autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-none h-12 pr-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-cyan-300"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 rounded-none font-mono uppercase tracking-wider bg-white/20 hover:bg-white/30 text-white border border-white/30 shadow-lg backdrop-blur-sm"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
